import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import ReplayIcon from '@mui/icons-material/Replay'
import { FS_MICRO, FS_XS, FS_MD, ICON_XS, ICON_MD } from '../../common/theme'
import { copyToClipboard } from '../../common/clipboardHelpers'
import type { ConvoMessage, TokenUsage } from '../../types'
import { MarkdownMessage } from '../../comps/MarkdownMessage'
import { ToolTimeline, ThinkingBlock, ToolBubbleConnector } from './ChatTimeline'
import { IterationGroup } from './IterationGroup'

// ─── Avatars ─────────────────────────────────────────────────────────────────

export function AiAvatar() {
  return (
    <Box sx={{ flexShrink: 0, mr: 1, mt: 0.25 }}>
      <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <SmartToyOutlinedIcon sx={{ fontSize: ICON_MD, color: '#fff' }} />
      </Box>
    </Box>
  )
}

function UserAvatar() {
  return (
    <Box sx={{ flexShrink: 0, ml: 1, mt: 0.25 }}>
      <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'grey.400', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <PersonOutlinedIcon sx={{ fontSize: ICON_MD, color: '#fff' }} />
      </Box>
    </Box>
  )
}


// ─── Token usage ─────────────────────────────────────────────────────────────

export function UsageRow({ usage }: { usage?: TokenUsage }) {
  if (!usage) return null
  const inTok = usage.input_tokens ?? 0
  const outTok = usage.output_tokens ?? 0
  const cacheRead = usage.cache_read_input_tokens ?? 0
  const cacheCreate = usage.cache_creation_input_tokens ?? 0
  if (!inTok && !outTok && !cacheRead && !cacheCreate) return null
  const cacheBits: string[] = []
  if (cacheRead) cacheBits.push(`${cacheRead.toLocaleString()} cached`)
  if (cacheCreate) cacheBits.push(`+${cacheCreate.toLocaleString()} cache write`)
  const tip = [
    `Input: ${inTok.toLocaleString()}`,
    `Output: ${outTok.toLocaleString()}`,
    ...(cacheBits.length ? cacheBits : []),
  ].join(' · ')
  return (
    <Tooltip title={tip}>
      <Typography variant="caption" sx={{ opacity: 0.45, fontSize: FS_MICRO }}>
        {(inTok + outTok).toLocaleString()} tokens
      </Typography>
    </Tooltip>
  )
}

// ─── Message footer (timestamp, copy, resend, usage) ────────────────────────

interface MessageFooterProps {
  msg: ConvoMessage
  index: number
  isProcessing: boolean
  onResend?: (index: number) => void
}

function MessageFooter({ msg, index, isProcessing, onResend }: MessageFooterProps) {
  const isUser = msg.type === 'user'
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isUser ? 'flex-end' : 'flex-start', gap: 0.5, mt: 0.25, px: 0.5 }}>

      {/* {!isUser && msg.usage && <UsageRow usage={msg.usage} />} */}
      {msg.updatetime && (
        <Typography variant="caption" sx={{ opacity: 0.45, fontSize: FS_XS }}>
          {typeof msg.updatetime === 'string' ? msg.updatetime.slice(11, 16) : ''}
        </Typography>
      )}
      {/* {!isUser && msg.usessources && (
        <Tooltip title="Used knowledge sources">
          <MenuBookOutlinedIcon sx={{ fontSize: ICON_MD, opacity: 0.5 }} />
        </Tooltip>
      )} */}
      {isUser && onResend && (
        <Tooltip title="Resend from here">
          <span>
            <IconButton size="small" data-testid="kcp_msg_resend_btn"
              disabled={isProcessing} onClick={() => onResend(index)}
              sx={{ p: 0.25, opacity: 0.45, '&:hover': { opacity: 1 } }}>
              <ReplayIcon sx={{ fontSize: ICON_XS }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Copy">
        <IconButton size="small" data-testid="kcp_msg_copy_btn"
          onClick={() => copyToClipboard(msg.q.replace(/<[^>]+>/g, ''))}
          sx={{ p: 0.25, opacity: 0.45, '&:hover': { opacity: 1 } }}>
          <ContentCopyOutlinedIcon sx={{ fontSize: ICON_XS }} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

// ─── User message ────────────────────────────────────────────────────────────

interface UserMessageProps {
  msg: ConvoMessage
  index: number
  isProcessing: boolean
  onResend?: (index: number) => void
}

export function UserMessage({ msg, index, isProcessing, onResend }: UserMessageProps) {
  return (
    <Box
      data-testid="kcp_msg_user"
      data-msg-index={index}
      sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}
    >
      <Box sx={{ maxWidth: '72%', minWidth: 0 }}>
        <Box sx={{
          px: 1.5, py: 1,
          borderRadius: '16px 16px 4px 16px',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}>
          <Box sx={{ fontSize: FS_MD, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
            {msg.q.split(/(@[^<]+<[^>]+>)/).map((part, pi) => {
              const match = part.match(/^@([^<]+)<([^>]+)>$/)
              if (match) return <Chip key={pi} label={`@${match[1]}`} size="small" sx={{ fontSize: '0.7rem', height: 20, bgcolor: 'primary.dark', color: '#fff', '& .MuiChip-label': { px: 1 } }} />
              return part ? <span key={pi}>{part}</span> : null
            })}
          </Box>
        </Box>
        <MessageFooter msg={msg} index={index} isProcessing={isProcessing} onResend={onResend} />
      </Box>
      <UserAvatar />
    </Box>
  )
}

// ─── Assistant message ───────────────────────────────────────────────────────

interface AssistantMessageProps {
  msg: ConvoMessage
  index: number
  isProcessing: boolean
}

export function AssistantMessage({ msg, index, isProcessing }: AssistantMessageProps) {
  return (
    <Box
      data-testid="kcp_msg_assistant"
      data-msg-index={index}
      sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}
    >
      <AiAvatar />
      <Box sx={{ maxWidth: '72%', minWidth: 0 }}>
        {msg.iterations?.length ? (
          msg.iterations.map((iter, idx) => (
            <IterationGroup
              key={idx}
              iter={iter}
              isFinal={idx === msg.iterations!.length - 1}
              hasNext={idx < msg.iterations!.length - 1}
              hasPrev={idx > 0}
              stoppedManually={idx === msg.iterations!.length - 1 && msg.stoppedManually}
            />
          ))
        ) : (
          <>
            {msg.thinking && (
              <ThinkingBlock thinking={msg.thinking} connectsBelow />
            )}
            {msg.tools?.length ? (
              <ToolTimeline
                tools={msg.tools}
                connectsAbove={!!msg.thinking}
                connectsToBubble
              />
            ) : null}
            {(msg.thinking || msg.tools?.length) && <ToolBubbleConnector />}
            <Box sx={{
              px: 1.5, py: 1,
              borderRadius: '16px 16px 16px 4px',
              bgcolor: 'action.selected',
              color: 'text.primary',
            }}>
              <MarkdownMessage content={msg.q} />
              {msg.stoppedManually && (
                <Box sx={{ mt: 0.75, pt: 0.5, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ fontSize: FS_MICRO, opacity: 0.55, fontStyle: 'italic' }}>User Interrupted</Typography>
                </Box>
              )}
            </Box>
          </>
        )}
        <MessageFooter msg={msg} index={index} isProcessing={isProcessing} />
      </Box>
    </Box>
  )
}
